import { FunctionComponent, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useClub } from '@/services/queries/club';
import { TEAMS_SEARCH_PAGE_SIZE } from '../../constants';
import { TeamSearchRow } from './TeamSearchRow';

const PREFIX = 'pages.application_season.components.season_step3_teams';

type TogglePayload = {
  uuid: string;
  name: string;
  acronym?: string;
  logo_path?: string;
};

type TeamsSearchPanelProps = {
  /** UUIDs já selecionados (para marcar checkbox e bloquear excedentes). */
  selectedTeamUuids: Set<string>;
  /** Limite máximo de seleção — bloqueia novas escolhas quando atingido. */
  maxReached: boolean;
  /** Recebe o objeto completo do club para popular o cache de metadados. */
  onToggle: (team: TogglePayload) => void;
};

export const TeamsSearchPanel: FunctionComponent<TeamsSearchPanelProps> = ({
  selectedTeamUuids,
  maxReached,
  onToggle,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const params = useMemo(
    () =>
      ({
        name: searchTerm.trim(),
        page: 1,
        limit: TEAMS_SEARCH_PAGE_SIZE,
        sort: 'name',
        order: 'asc',
      }) as unknown as Parameters<typeof useClub>[0],
    [searchTerm],
  );

  const { data, isFetching } = useClub(params);
  const items = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  return (
    <Flex direction="column" gap={2} minW={0}>
      <Flex align="center" justify="space-between" gap={2}>
        <Flex align="center" gap={2} color="gray.500">
          <ChakraFeatherIcon name="search" boxSize="14px" />
          <Text
            fontSize="12px"
            fontWeight="semibold"
            letterSpacing="wide"
            textTransform="uppercase"
          >
            {t(`${PREFIX}.search.title`)}
          </Text>
        </Flex>
        <Badge variant="subtle" colorScheme="gray" borderRadius="full" px={2}>
          {t(`${PREFIX}.search.total`, { count: total })}
        </Badge>
      </Flex>

      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <ChakraFeatherIcon name="search" boxSize="14px" color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder={t(`${PREFIX}.search.placeholder`)}
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
        />
      </InputGroup>

      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        maxH="340px"
        overflowY="auto"
      >
        {items.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap={1}
            py={8}
            color="gray.500"
          >
            <ChakraFeatherIcon name="search" boxSize="20px" />
            <Text fontSize="13px">
              {isFetching ? t(`${PREFIX}.search.loading`) : t(`${PREFIX}.search.empty`)}
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" p={1} gap={0.5}>
            {items.map(team => {
              const isSelected = selectedTeamUuids.has(team.uuid);
              return (
                <TeamSearchRow
                  key={team.uuid}
                  uuid={team.uuid}
                  name={team.name}
                  acronym={team.acronym}
                  logoPath={team.logo_path}
                  isSelected={isSelected}
                  isDisabled={!isSelected && maxReached}
                  onToggle={() =>
                    onToggle({
                      uuid: team.uuid,
                      name: team.name,
                      acronym: team.acronym,
                      logo_path: team.logo_path,
                    })
                  }
                />
              );
            })}
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
